import React, { useState, useRef } from 'react';
import { collection, getDocs, writeBatch, doc, arrayUnion, arrayRemove } from '../../../lib/auditedFirestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../lib/firebase';

const BulkAddStudents = ({ addedBy, setModal }) => {
    const [studentStatuses, setStudentStatuses] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [operationMode, setOperationMode] = useState('create'); 

    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim() !== '');
            const parsedData = lines.slice(1).map(line => {
                const fields = line.split(',').map(field => field.trim());
                if (operationMode === 'create') {
                    return { 
                        email: fields[0], 
                        password: fields[1], 
                        paper: fields[2], 
                        name: fields[3], 
                        phone: fields[4] || '', 
                        status: 'pending', 
                        message: '' 
                    };
                } else {
                    return { email: fields[0], paper: fields[1], status: 'pending', message: '' };
                }
            });
            setStudentStatuses(parsedData);
        };
        reader.readAsText(file);
    };

    const startProcessing = async () => {
        setIsProcessing(true);
        setStudentStatuses(prev => prev.map(s => ({ ...s, status: 'processing', message: 'Processing...' })));

        if (operationMode === 'create') {
            await handleBulkCreate();
        } else {
            await handleBulkPapers(operationMode);
        }
        setIsProcessing(false);
    };

    const handleBulkCreate = async () => {
        // Removed sheetId from the payload entirely
        const studentsToCreate = studentStatuses.map(s => ({
            email: s.email,
            password: s.password,
            paperName: s.paper, 
            name: s.name,
            phone: s.phone
        }));

        try {
            const bulkCreateStudents = httpsCallable(functions, 'bulkCreateStudents');
            const response = await bulkCreateStudents({ students: studentsToCreate, addedBy });
            const { results } = response.data;

            setStudentStatuses(prev => prev.map(originalStudent => {
                const result = results.find(r => r.email === originalStudent.email);
                return result ? { ...originalStudent, status: result.status, message: result.message } : originalStudent;
            }));
            setModal({ show: true, message: 'Bulk creation completed. Check statuses below.' });
        } catch (error) {
            console.error("Cloud function error:", error);
            setModal({ show: true, message: `An error occurred: ${error.message}` });
            setStudentStatuses(prev => prev.map(s => ({ ...s, status: 'error', message: 'System error.' })));
        }
    };

    const handleBulkPapers = async (mode) => {
        try {
            const usersSnap = await getDocs(collection(db, 'users'));
            const papersSnap = await getDocs(collection(db, 'papers'));
            
            const userMap = {};
            usersSnap.forEach(doc => { userMap[doc.data().email.toLowerCase()] = doc.id });
            
            const paperMap = {};
            papersSnap.forEach(doc => { paperMap[doc.data().name.toLowerCase()] = doc.id });

            let batch = writeBatch(db);
            let operationCount = 0;
            const updatedStatuses = [...studentStatuses];

            for (let i = 0; i < updatedStatuses.length; i++) {
                const item = updatedStatuses[i];
                const userId = userMap[item.email.toLowerCase()];
                const paperId = paperMap[item.paper.toLowerCase()];

                if (!userId) {
                    item.status = 'error'; item.message = 'User not found'; continue;
                }
                if (!paperId) {
                    item.status = 'error'; item.message = 'Paper not found'; continue;
                }

                const userRef = doc(db, 'users', userId);
                batch.update(userRef, {
                    // This strictly appends or removes papers. It NEVER deletes the user document.
                    assignedPapers: mode === 'add_papers' ? arrayUnion(paperId) : arrayRemove(paperId)
                });
                
                item.status = 'success';
                item.message = mode === 'add_papers' ? 'Paper Access Added' : 'Paper Access Removed';
                operationCount++;

                if (operationCount % 450 === 0) {
                    await batch.commit();
                    batch = writeBatch(db);
                }
            }
            
            if (operationCount % 450 !== 0) {
                await batch.commit(); 
            }

            setStudentStatuses(updatedStatuses);
            setModal({ show: true, message: `Successfully updated paper access.` });
            
        } catch (error) {
            console.error("Batch update error:", error);
            setModal({ show: true, message: `Error updating papers: ${error.message}` });
        }
    };

    const StatusIndicator = ({ status }) => {
        switch (status) {
            case 'pending': return <span className="text-xs font-medium text-gray-500">Pending</span>;
            case 'processing': return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>;
            case 'success': return <span className="text-xs font-medium text-green-600">Success</span>;
            case 'error': return <span className="text-xs font-medium text-red-600">Failed</span>;
            default: return null;
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow mt-8 border border-gray-100">
            <h3 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Bulk CSV Operations</h3>
            
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Operation Type</label>
                <select 
                    value={operationMode} 
                    onChange={(e) => { 
                        setOperationMode(e.target.value); 
                        setStudentStatuses([]); 
                        if(fileInputRef.current) fileInputRef.current.value = ""; 
                    }}
                    className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                    <option value="create">Create New Users</option>
                    <option value="add_papers">Bulk Add Paper Access</option>
                    <option value="remove_papers">Bulk Remove Paper Access</option>
                </select>
            </div>

            <div className="bg-blue-50 text-blue-800 p-3 rounded text-sm mb-4">
                <strong>Format required: </strong>
                {operationMode === 'create' 
                    ? "CSV with columns: `email`, `password`, `paper`, `name`, `phone` (First row must be header)." 
                    : "CSV with columns: `email`, `paper` (First row must be header)."}
            </div>

            <div className="flex flex-col md:flex-row gap-4 items-center">
                <input 
                    type="file" 
                    accept=".csv" 
                    ref={fileInputRef} 
                    onChange={handleFileChange} 
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
                <button 
                    onClick={startProcessing} 
                    disabled={isProcessing || studentStatuses.length === 0} 
                    className="w-full md:w-auto whitespace-nowrap bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-2 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:from-purple-300 disabled:to-pink-300"
                >
                    {isProcessing ? 'Processing...' : 'Start Execution'}
                </button>
            </div>

            {studentStatuses.length > 0 && (
                <div className="mt-6 max-h-64 overflow-y-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                {operationMode === 'create' && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>}
                                {operationMode === 'create' && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>}
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paper</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {studentStatuses.map((student, index) => (
                                <tr key={index}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">{student.email}</td>
                                    {operationMode === 'create' && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">{student.name || 'N/A'}</td>}
                                    {operationMode === 'create' && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">{student.phone || 'N/A'}</td>}
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">{student.paper}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm"><StatusIndicator status={student.status} /></td>
                                    <td className={`px-4 py-2 whitespace-nowrap text-xs ${student.status === 'error' ? 'text-red-600' : 'text-gray-500'}`}>{student.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
export default BulkAddStudents;